/**
 * Display formatters (Block 6.A / F-B + F-C).
 *
 * F-B: pg `date` columns (provider_individual_organizations.start_date /
 * .end_date per migration 0047) serialize as ISO-8601 with
 * `T00:00:00.000Z` suffix when the pg client returns Date objects and
 * JSON.stringify rounds them to UTC midnight. UI strips the time
 * component for display so users see "2024-02" not
 * "2024-02-01T00:00:00.000Z".
 *
 * Timestamps (`created_at`, `updated_at`, `last_verified_at`,
 * `archived_at`, `last_updated`) carry meaningful time data — those use
 * the timestamp formatters, not the date formatters.
 */

/**
 * Format an affiliation date for display ("YYYY-MM-DD" → "YYYY-MM").
 * Returns "—" for null. Strips F-B's T-component first so we never
 * accidentally format the time portion.
 */
export function formatAffiliationDate(value: string | null): string {
  if (value === null) return '–';
  // F-B: strip the "T..." portion if present, then take YYYY-MM.
  const dateOnly = value.split('T')[0];
  if (!dateOnly) return '–';
  const parts = dateOnly.split('-');
  if (parts.length < 2) return dateOnly;
  return `${parts[0]}-${parts[1]}`;
}

/**
 * Format a date range "start → end" for affiliation display.
 * Open-ended (end_date null) renders "→ present".
 */
export function formatAffiliationRange(
  startDate: string | null,
  endDate: string | null,
): string {
  const start = formatAffiliationDate(startDate);
  if (endDate === null) {
    return `${start} → present`;
  }
  return `${start} → ${formatAffiliationDate(endDate)}`;
}

/**
 * Format a timestamptz value as a human-readable date ("Apr 15, 2026").
 * Returns "never" for null.
 */
export function formatTimestamp(value: string | null): string {
  if (value === null) return 'never';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'invalid date';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a non-negative record count for the admin lookup row's
 * record_count column (Block 6.C.1 / plan §11.2 LOCK).
 *
 * Phase 1 lock: `Intl.NumberFormat` comma-format only. Empirical Q-S4
 * (peace_os_dev 2026-05-08) surfaced max record_count = 341, well under
 * the 99,999 abbreviation threshold; lock holds. Future operator-data
 * surfacing > 99,999 escalates to abbreviation via amendment to this
 * function (not a 6.C.1 surface).
 */
const RECORD_COUNT_FORMATTER = new Intl.NumberFormat('en-US');
export function formatRecordCount(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '–';
  return RECORD_COUNT_FORMATTER.format(n);
}

/**
 * Format a byte count for attachment display.
 * <1KB → "N B"; <1MB → "N KB" (rounded); else "N.N MB" (1 decimal).
 * Mirrors peace-hub/src/components/ResourceDetail.jsx:94-98.
 *
 * Block 6.F.3 — used by AttachmentsGallery in the peace-hub-port. The
 * gallery itself ships UI-skeleton-only in 6.F.3 (renders empty); the
 * substrate (provider_attachments table + Blob storage + signed-URL)
 * lands at forward sub-block 6.F.3.5.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Strip non-digits from a phone string for tel: URL construction.
 * Mirrors peace-hub/src/utils/constants.js:51-54.
 *
 * Block 6.F.3 — disambiguated from formatPhoneDisplay below per L4
 * audit-finding fix. tel: URLs need digits-only; visible labels need
 * parens-formatted output. Both functions land in the same file so
 * imports stay co-located.
 */
export function formatPhoneDigits(phone: string | null): string {
  if (!phone) return '';
  return phone.replace(/[^\d+]/g, '');
}

/**
 * Format a phone string for display: "(XXX) XXX-XXXX" if 10 digits;
 * pass-through otherwise. Mirrors the inline impl at
 * peace-hub/src/components/ContactView.jsx:12-19 + ContactCard.jsx:6-13.
 */
export function formatPhoneDisplay(phone: string | null): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

/**
 * "3 months ago" relative time for last_verified_at staleness display.
 * Returns "never" for null.
 */
export function formatRelative(value: string | null): string {
  if (value === null) return 'never';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'invalid date';
  const ms = Date.now() - d.getTime();
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days < 1) return 'today';
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  }
  if (days < 365) {
    const months = Math.floor(days / 30);
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}
